import "./PageOverview.scss";

import clsx from "clsx";
import { compareDesc } from "date-fns";
import { FC, useCallback, useContext, useEffect, useLayoutEffect, useMemo, useState } from "react";

import { AssessmentSummaryMainPanel } from "../assessment/AssessmentSummaryMainPanel";
import { LAST_ASSESSMENT_PANEL_ACCESS_ID } from "../assessment/helpers";

import { PageTitlebar } from "./PageTitlebar";

import { DisplayCaption } from "components/DisplayCaption";
import { useAITool } from "components/ai/hooks/use-ai-tool";
import {
  ACCESS_ID_QUERY_PARAM,
  SECONDARY_OVERLAY_QUERY_PARAM,
  SUBMISSION_ID_QUERY_PARAM,
} from "components/constants";
import { PageDetailView } from "components/materials/page/content/PageDetailView";
import { PageOverviewHeader } from "components/materials/page/content/PageOverviewHeader";
import {
  SELECTED_ACCESS_ID_PAGE_TABS,
  getPageVariantLabel,
  isInLateSubmissionWindow,
  isPastSubmissionDueDate,
} from "components/materials/page/helpers";
import { FeatureModal } from "components/materials/presentation/featuring/FeatureModal";
import { ProjectorScroller } from "components/materials/presentation/projector/ProjectorScroller";
import { useIsAssessing } from "components/materials/section/assessment/hooks";
import type { PageType, SubmissionType } from "components/server-types";
import { InteractionMode } from "components/server-types";
import { t } from "i18n/i18n";
import { IconText } from "mds/components/IconText";
import { TabBar, type TabItem } from "mds/components/TabBar";
import { useIsMdOrLarger } from "mds/hooks/use-responsive";
import {
  FolderIcon,
  LongFormPencilIcon,
  PageIcon,
  PersonClassIcon,
  PresentPlayIcon,
} from "mds/icons";
import { BypassReleaseWarningContext } from "providers/BypassReleaseWarningProvider";
import { type PageTabVariant } from "providers/DocViewProvider";
import {
  useDocVersionDiverged,
  useWorksheetLoaded,
  useWorksheetPermissionRefresh,
} from "sharedb/ShareDBDoc";
import { useCapabilities } from "store/capability-hooks";
import { storeApi, useAppDispatch, useAppSelector } from "store/index";
import {
  pageSelectors,
  selectAssessmentsByPageId,
  selectCurrentSectionIsTemplate,
  selectCurrentSectionUserId,
  selectIsEditModeEnabled,
  selectIsRecordingAgent,
  selectIsTimeline,
  selectOfficialSubmissionBySubmissionId,
  selectPageAccessDataForSectionUser,
  selectProjectorHeight,
  selectSelectedPageTabVariant,
  selectShouldResetPageTab,
  selectUserSubmissionsByPageId,
  submissionSelectors,
} from "store/selectors";
import { selectIsInBreakout } from "store/selectors/breakouts";
import { viewStateActions } from "store/slices/view";
import { useListSelector } from "store/store-hooks";
import { useAv } from "utils/av";
import { logInfoWithDebugFlag, rollbarAndLogError } from "utils/logger";
import {
  isGroupPage,
  isIndividualPage,
  isPageDisabled,
  isSharedClassPage,
  isStaticPage,
} from "utils/page-utils";
import {
  FeatureStatus,
  PROJECTOR_MAX_HEIGHT,
  usePresentation,
  usePresentationCaption,
} from "utils/presentation";
import { useQueryParamsManager } from "utils/routing";

const PAGE_HEADER_HEIGHT = 80;
const TOP_NAV_BAR_HEIGHT = 53;
const EXTRA_PADDING = 16;

type ProjectorOverlayProps = {
  shouldShowProjectorOverlay: boolean;
  projectorHeight: number;
  captionText: string;
  captionBgColor: string;
};

const ProjectorOverlay: FC<ProjectorOverlayProps> = ({
  shouldShowProjectorOverlay,
  projectorHeight,
  captionText,
  captionBgColor,
}) => {
  // The page header is included in the projector overlay but is not visible in the projector view, so we need to add it to the height here.
  const height = projectorHeight + PAGE_HEADER_HEIGHT;

  return shouldShowProjectorOverlay ? (
    <>
      <div
        className="pointer-events-none absolute bottom-0 left-1/2 top-0 z-10 -translate-x-1/2 transform border-[2px] border-green-tint-20"
        style={{
          height: projectorHeight ? `min(${height}px, 100%)` : "100%",
          maxWidth: "1150px",
          width: "100%",
        }}
      >
        <DisplayCaption bgColorClass={captionBgColor} caption={captionText} />
      </div>

      <div
        className="absolute inset-0 z-10 overflow-hidden rounded-[inherit] bg-black-tint-95/80 backdrop-blur-sm"
        style={{
          WebkitMaskImage: `
                    linear-gradient(to bottom, transparent 0px, transparent ${height}px, black ${height}px, black 100%),
                    linear-gradient(to right, black 0, black calc(50% - 575px), transparent calc(50% - 575px), transparent calc(50% + 575px), black calc(50% + 575px), black 100%)
                  `,
          maskImage: `
                    linear-gradient(to bottom, transparent 0px, transparent ${height}px, black ${height}px, black 100%),
                    linear-gradient(to right, black 0, black calc(50% - 575px), transparent calc(50% - 575px), transparent calc(50% + 575px), black calc(50% + 575px), black 100%)
                  `,
          WebkitMaskComposite: "source-over, destination-out",
          maskComposite: "add, exclude",
          WebkitMaskRepeat: "no-repeat",
          maskRepeat: "no-repeat",
          WebkitMaskSize: "100% 100%",
          maskSize: "100% 100%",
          pointerEvents: "none",
        }}
      />
    </>
  ) : null;
};

type PageOverviewProps = {
  pageId: string;
  selectedTopicId?: string;
  pages: PageType[];
};

// TODO: See if it makes sense to separate assessing into it's own overview component
export const PageOverview: FC<PageOverviewProps> = ({ pageId, selectedTopicId, pages }) => {
  const [dataLoaded, setDataLoaded] = useState<boolean>(false);
  const { deleteQueryParams, getQueryParam } = useQueryParamsManager();
  const selectedAccessId = getQueryParam(ACCESS_ID_QUERY_PARAM);
  const submissionId = getQueryParam(SUBMISSION_ID_QUERY_PARAM);
  const assessments = useAppSelector((s) => selectAssessmentsByPageId(s, pageId));
  const shouldResetPageTab = useAppSelector(selectShouldResetPageTab);
  const isEmptyPanel = selectedAccessId === LAST_ASSESSMENT_PANEL_ACCESS_ID;
  const projectorHeight = useAppSelector(selectProjectorHeight) || PROJECTOR_MAX_HEIGHT;

  const { canAuthorSection, canParticipateInSection } = useCapabilities();
  const page = useAppSelector((s) => pageSelectors.selectById(s, pageId));
  const isAssessing = useIsAssessing();
  const isEditModeEnabled = useAppSelector(selectIsEditModeEnabled);
  const submissions = useListSelector((s) => selectUserSubmissionsByPageId(s, pageId));
  const sortedSubmissions = submissions.toSorted((a, b) => compareDesc(a.created_at, b.created_at));
  const sectionUserId = useAppSelector(selectCurrentSectionUserId);
  const pageAccessData = useAppSelector((s) =>
    selectPageAccessDataForSectionUser(s, pageId, sectionUserId),
  );
  const tabVariant = useAppSelector(selectSelectedPageTabVariant);
  const dispatch = useAppDispatch();
  const isRecordingAgent = useAppSelector(selectIsRecordingAgent);
  const isInTemplateSection = useAppSelector(selectCurrentSectionIsTemplate);
  const isInBreakout = useAppSelector(selectIsInBreakout);
  const isTimeline = useAppSelector(selectIsTimeline);

  // For marking unseen submissions as seen when viewing in assessment mode
  const currentSubmission = useAppSelector((s) => submissionSelectors.selectById(s, submissionId));
  const officialSubmission = useAppSelector((s) =>
    selectOfficialSubmissionBySubmissionId(s, submissionId),
  );

  const {
    featureStatus,
    hasProjectorView,
    isFollowing,
    isPresenting,
    presentedPageId,
    presentingRollup,
  } = usePresentation();
  const { captionText, captionBgColor } = usePresentationCaption();

  const { isInVirtualClass } = useAv();
  const isSmallScreen = !useIsMdOrLarger();
  const isInVirtualClassOnMobile = isInVirtualClass && isSmallScreen;

  const pageIsPresenting = presentedPageId === page?.id;
  const worksheetId = page?.worksheet_id || null;

  useAITool("modify_page_content", canAuthorSection);
  useAITool("modify_page_tagged_outcomes", canAuthorSection);
  useAITool("modify_page_rubric", canAuthorSection);

  useWorksheetPermissionRefresh(pageId);

  const sectionId = page?.section_id;
  const pageIsStatic = isStaticPage(page);
  const isSharedPage = isSharedClassPage(page);

  const canSeeStudentDropdownOnTab =
    !isSharedPage &&
    // We are allowing the instructor to see the student dropdown on a page with static content, because:
    // 1) we've allowed the instructor to collect submissions on a page with static content (to be fixed here https://app.asana.com/0/1206079475640061/1208403523398930)
    // 2) if the instructor deletes all interactive blocks on the page after submissions are collected, we still want to show the submission list dropdown to the instructor in assessment mode
    (!pageIsStatic || isAssessing) &&
    canAuthorSection;
  const canViewClassResponses =
    canSeeStudentDropdownOnTab || Boolean(page?.class_responses_released_at);

  const isResponsesTabVisible = !isAssessing && canViewClassResponses && !isInTemplateSection;
  const isMySubmissionTabVisible = submissions.length > 0 && canParticipateInSection;
  const isStudentSubmissionTabVisible = isAssessing;
  const isProjecting = isPresenting && Boolean(hasProjectorView);
  const isEditingPage = !pageAccessData?.completed_at;
  const dueAt = page?.due_at || null;
  const hasDueDate = !!dueAt;
  const isPastDueDate = isPastSubmissionDueDate(page);
  const isWithinLateSubmissionPeriod = isInLateSubmissionWindow(page);
  const hasSubmissionBeenEdited = useDocVersionDiverged(
    worksheetId,
    sortedSubmissions[0]?.worksheet_sharedb_version,
  );
  // The Workspace tab should be hidden if the student has a submission and is not editing the page.
  // Special case: if the page has no due date, or the late submission period has passed, and the
  // submission was edited at some point, always show the Workspace tab.
  const isStudentWorkspaceTabVisible =
    !(isMySubmissionTabVisible && !isEditingPage) ||
    (!hasDueDate && hasSubmissionBeenEdited) ||
    (isPastDueDate && !isWithinLateSubmissionPeriod && hasSubmissionBeenEdited);

  const haveSubmissionsBeenCollected = Boolean(page?.submissions_last_collected_at);
  // For pages with a due date in the future, if submissions have not yet been collected, we "fake"
  // a submission from the student's perspective in the UI but do not actually create one in the system.
  const isFakeSubmission =
    hasDueDate && !isPastDueDate && !haveSubmissionsBeenCollected && !isEditingPage;

  // We probably don't need to filter by submission here, but it's possible we could see someone else's
  // assessment if it's being featured.
  const hasAssessment =
    isMySubmissionTabVisible &&
    submissions.some((s: SubmissionType) => assessments.some((a) => a.submission_id === s.id));

  // Keep in sync with tabs.
  const shownTabVariants = useMemo(() => {
    const shownTabs: PageTabVariant[] = [];

    if (isRecordingAgent) {
      return ["page_content"] as PageTabVariant[];
    }

    if (canAuthorSection) {
      shownTabs.push("page_content");
    } else if (page?.interaction_mode === InteractionMode.SHARED_CLASS) {
      shownTabs.push("class_draft");
    } else if (isStudentWorkspaceTabVisible) {
      shownTabs.push("my_draft");
    }

    if (isMySubmissionTabVisible) {
      shownTabs.push("my_submission");
    }

    if (isResponsesTabVisible) {
      shownTabs.push("responses");
    }

    if (isStudentSubmissionTabVisible) {
      shownTabs.push("student_submission");
    }

    return shownTabs;
  }, [
    isRecordingAgent,
    canAuthorSection,
    page?.interaction_mode,
    isStudentWorkspaceTabVisible,
    isMySubmissionTabVisible,
    isResponsesTabVisible,
    isStudentSubmissionTabVisible,
  ]);

  const defaultTabVariant = isAssessing
    ? "student_submission"
    : isResponsesTabVisible && selectedAccessId
      ? "responses"
      : isMySubmissionTabVisible && (submissionId || hasAssessment)
        ? "my_submission"
        : shownTabVariants[0];

  const setTab = useCallback(
    (variant: PageTabVariant) => {
      if (!shownTabVariants.includes(variant)) {
        rollbarAndLogError(new Error(`setTab called with invalid tab variant`), {
          variant,
          availableTabs: shownTabVariants.join(", "),
        });
        return;
      }

      if (variant === tabVariant) {
        return;
      }

      dispatch(viewStateActions.setSelectedPageTabVariant(variant));

      // Need to clear submission data so it doesn't block editing.
      if (
        ["page_content", "my_draft"].includes(variant) ||
        (canParticipateInSection && variant === "responses")
      ) {
        deleteQueryParams([SUBMISSION_ID_QUERY_PARAM, ACCESS_ID_QUERY_PARAM]);
      } else if (variant === "my_submission") {
        deleteQueryParams([SECONDARY_OVERLAY_QUERY_PARAM]);
      }
    },
    [shownTabVariants, tabVariant, dispatch, canParticipateInSection, deleteQueryParams],
  );

  const presentedTabVariant = pageIsPresenting
    ? presentingRollup
      ? "responses"
      : shownTabVariants[0]
    : undefined;
  const presentingTab = tabVariant === presentedTabVariant;
  const presentingOtherTab = pageIsPresenting && !presentingTab;
  const featuring =
    pageIsPresenting && featureStatus !== FeatureStatus.NONE && (isPresenting || isFollowing);

  const shouldSyncScrolling =
    isProjecting && pageIsPresenting && !featuring && tabVariant !== "responses";

  const isFeaturing = featureStatus !== FeatureStatus.NONE;
  const shouldShowProjectorOverlay = shouldSyncScrolling && !isFeaturing;

  useEffect(() => {
    // If the page was deleted, the pageId doesn't resolve to a page anymore and we don't
    // want to trigger side effects.
    if (!page?.id) {
      return;
    }

    logInfoWithDebugFlag(
      "debug_sharedb",
      `[PageOverview] page: ${page?.id}, worksheet_id: ${page?.worksheet_id}`,
    );

    // Load the page groups or section users depending on the type of page
    if (isGroupPage(page)) {
      storeApi.page_groups.list({ page_id: page.id });
      storeApi.page_group_users.list({ page_group__page_id: page.id });
    } else if (isIndividualPage(page)) {
      storeApi.section_users.list({ section_id: sectionId });
    }
    storeApi.page_access_data.list({ page_id: page.id });
  }, [page, sectionId]);

  const { resetWarning } = useContext(BypassReleaseWarningContext);

  const isDocLoaded = useWorksheetLoaded(page?.worksheet_id);

  useEffect(() => {
    const loadData = async () => {
      // Load the submissions, assessments and outcome assessments for the page.
      if (pageId) {
        await Promise.all([
          storeApi.submissions.list({ page_id: pageId }),
          storeApi.assessments.list({ submission__page_id: pageId }),
          storeApi.outcome_assessments.list({ assessment__submission__page_id: pageId }),
          // TODO: Make these conditional based on page type.
          storeApi.page_groups.list({ page_id: pageId }),
          storeApi.page_group_users.list({ page_group__page_id: pageId }),
        ]);

        setDataLoaded(true);
      }
    };

    setDataLoaded(false);
    loadData();

    // Reset the "bypass warning" state when the page changes.
    resetWarning();
  }, [pageId, resetWarning]);

  // Reset tab index when page changes or when presenting
  // and switching between responses and template page.
  useLayoutEffect(() => {
    if (dataLoaded && isDocLoaded) {
      setTab(defaultTabVariant);
    }
    // Only want to do this on page change
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dataLoaded, isDocLoaded, pageId]);

  // Reset the tab index if the instructor has clicked the
  // "Return" button from the footer bar.
  useEffect(() => {
    if (shouldResetPageTab) {
      setTab(defaultTabVariant);
      dispatch(viewStateActions.setShouldResetPageTab(false));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shouldResetPageTab]);

  // Set the tab index to student_submission when
  // entering assessment mode.
  useEffect(() => {
    if (isAssessing) {
      setTab(defaultTabVariant);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAssessing]);

  // When the available tabs change (e.g. role change from instructor to student),
  // reset to default if the current selection is no longer valid.
  useEffect(() => {
    if (tabVariant != null && !shownTabVariants.includes(tabVariant)) {
      setTab(defaultTabVariant);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shownTabVariants, tabVariant]);

  // Navigating between pages resets the timeline view state, unless the URL has timeline=1.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    dispatch(viewStateActions.setIsTimelineView(params.get("timeline") === "1"));
  }, [page?.id, dispatch]);

  // Mark unseen new submissions as seen when an instructor views them in assessment mode
  useEffect(() => {
    if (!isAssessing || !canAuthorSection || !currentSubmission || !officialSubmission) {
      return;
    }

    // Check if current submission is newer than official and hasn't been seen
    const isNewerThanOfficial =
      new Date(currentSubmission.created_at) > new Date(officialSubmission.created_at);

    if (isNewerThanOfficial && !currentSubmission.seen_after_assessed_at) {
      storeApi.submissions.partial_update({
        id: currentSubmission.id,
        seen_after_assessed_at: new Date().toISOString(),
      });
    }
  }, [isAssessing, canAuthorSection, currentSubmission, officialSubmission]);

  const tabs: TabItem[] = useMemo(() => {
    const shownTabs: TabItem[] = [];

    shownTabVariants.forEach((shownTabVariant) => {
      const tabLabel = getPageVariantLabel(shownTabVariant, isGroupPage(page), isFakeSubmission);

      if (
        shownTabVariant === "my_draft" ||
        shownTabVariant === "class_draft" ||
        shownTabVariant === "page_content"
      ) {
        const tabTitle =
          shownTabVariant === "my_draft" && canParticipateInSection
            ? t("tooltip.instructor_can_view_draft")
            : tabLabel;
        const isPresentingThisTab = isPresenting && presentedTabVariant === shownTabVariants[0];

        shownTabs.push({
          label: (
            <IconText
              className={isAssessing ? "cursor-not-allowed text-black-tint-55" : ""}
              iconStart={
                isPresentingThisTab ? (
                  <PresentPlayIcon className="group-data-[selected]/tab:text-green" />
                ) : shownTabVariant === "page_content" ? (
                  <PageIcon className="group-data-[selected]/tab:text-blue" />
                ) : isFakeSubmission ? (
                  <FolderIcon className="group-data-[selected]/tab:text-blue" />
                ) : (
                  <LongFormPencilIcon className="group-data-[presented]/tab:group-data-[selected]/tab:text-green group-data-[presented]/tab:text-green group-data-[selected]/tab:text-blue" />
                )
              }
              size="xs"
              text={tabLabel}
              hideIconOnSm
            />
          ),
          eventName: `Page - View ${shownTabVariant} tab`,
          className: clsx({
            presented: presentedTabVariant === shownTabVariants[0],
          }),
          extraProps: {
            "data-presented": presentedTabVariant === shownTabVariants[0] || undefined,
          },
          title: tabTitle,
          tabVariant: shownTabVariant,
          disabled: isAssessing || isTimeline,
        });
      }

      if (shownTabVariant === "my_submission") {
        // This is a student-only tab to see their submissions if they have any.
        shownTabs.push({
          label: (
            <IconText
              iconStart={<FolderIcon className="group-data-[selected]/tab:text-blue" />}
              size="xs"
              text={tabLabel}
              hideIconOnSm
            />
          ),
          title: tabLabel,
          eventName: `Page - View ${shownTabVariant} tab`,
          tabVariant: shownTabVariant,
        });
      }

      if (shownTabVariant === "student_submission" && canSeeStudentDropdownOnTab) {
        // This is an instructors-only tab in assessment mode and can't be presented. They
        // shouldn't be able to enter assessment mode and present at the same time.
        shownTabs.push({
          label: tabLabel,
          title: tabLabel,
          eventName: `Page - View ${shownTabVariant} tab`,
          tabVariant: shownTabVariant,
        });
      }

      if (shownTabVariant === "responses" && canViewClassResponses) {
        // Add "released" indicator to tab label if user can author and responses are released
        const responsesTabLabel =
          canAuthorSection && page?.class_responses_released_at ? (
            <div className="flex items-center gap-2">
              <PersonClassIcon className="hidden group-data-[selected]/tab:text-blue md:block" />
              <span>{tabLabel}</span>
              <div className="text-s rounded-2xl bg-green-tint-90 px-2 py-[2px] font-medium text-green-shade-40">
                <span>{t("common.released")}</span>
              </div>
            </div>
          ) : (
            <div className="flex items-center gap-2">
              <PersonClassIcon className="hidden group-data-[selected]/tab:text-blue md:block" />
              <span>{tabLabel}</span>
            </div>
          );

        shownTabs.push({
          label: responsesTabLabel,
          className: clsx({
            presented: presentedTabVariant === "responses",
          }),
          eventName: `Page - View ${shownTabVariant} tab`,
          title: tabLabel,
          tabVariant: "responses",
          disabled: isTimeline,
        });
      }
    });

    return shownTabs;
  }, [
    shownTabVariants,
    page,
    isFakeSubmission,
    canSeeStudentDropdownOnTab,
    canViewClassResponses,
    canParticipateInSection,
    isPresenting,
    isTimeline,
    presentedTabVariant,
    isAssessing,
    canAuthorSection,
  ]);

  const contentOnly = !canAuthorSection && pageIsStatic;

  return (
    <main className="relative flex h-full w-full flex-col items-center rounded-[inherit] @container">
      <div className="relative z-0 flex h-full w-full shrink-[2] grow-[2] basis-2/3 flex-col items-center bg-white">
        {/* The recording agent automatically follows the presentation or screensharing.
            When isFollowing is false, it means that there is no presentation or screensharing going on.
            In this case and in case of a breakout, we just show a simple message. */}
        {isRecordingAgent && (!isFollowing || isInBreakout) ? (
          <div className="flex h-full w-full flex-col items-center justify-center">
            <div className="text-center text-lg font-medium">
              {isInBreakout ? t("recording_agent.in_breakout") : t("recording_agent.no_content")}
            </div>
          </div>
        ) : (
          <>
            {!isInVirtualClassOnMobile && (
              <PageTitlebar page={page} pages={pages} selectedTopicId={selectedTopicId} />
            )}

            <ProjectorScroller
              className="page-overview flex w-full flex-grow flex-col items-center"
              pageId={page?.id}
              shouldScroll={shouldSyncScrolling}
              shouldShowBackToTop={!shouldShowProjectorOverlay}
            >
              {isInVirtualClassOnMobile && (
                <PageTitlebar page={page} pages={pages} selectedTopicId={selectedTopicId} />
              )}

              {page && (
                <PageOverviewHeader
                  isStaticContent={pageIsStatic}
                  page={page}
                  selectedAccessId={
                    // TODO: This shouldn't be necessary if we are correctly unsetting the selectedAccessId.
                    //  We should check all edge cases (switching between pages, switching between tabs,
                    //  selecting dropdowns options into switching tabs before removing this).
                    tabVariant && SELECTED_ACCESS_ID_PAGE_TABS.includes(tabVariant)
                      ? selectedAccessId
                      : undefined
                  }
                />
              )}

              <div
                className={clsx("mt-2 flex w-full flex-col items-center px-4 md:px-10", {
                  presenting: presentingTab,
                  "presenting-other-tab": presentingOtherTab,
                })}
                style={{
                  // Calculates the exact amount of extra space we need to add to the bottom of the page
                  // so that we can appear to scroll to the bottom within the projector overlay box.
                  marginBottom: shouldShowProjectorOverlay
                    ? `max(calc(100vh - ${projectorHeight + PAGE_HEADER_HEIGHT}px - var(--footer-height) - ${TOP_NAV_BAR_HEIGHT}px + ${EXTRA_PADDING}px), ${EXTRA_PADDING}px)`
                    : undefined,
                }}
              >
                {/* Default index should be the student responses tab in assessment mode. */}
                <TabBar
                  className={clsx("w-full max-w-[--page-default-max-width]", {
                    "mt-4 border-b-2 border-blue-tint-90 md:mb-6": contentOnly,
                  })}
                  contentOnly={contentOnly}
                  selectedIndex={tabVariant ? shownTabVariants.indexOf(tabVariant) : 0}
                  tabs={tabs}
                  onChange={(index) => setTab(tabs[index].tabVariant as PageTabVariant)}
                />

                {/* We hide the tab panel content and instead control the content via props here.
                This allows us to avoid conditionally render multiple worksheets, which often broke. */}
                <div
                  className={clsx("w-full", {
                    "edit-mode":
                      isEditModeEnabled && tabVariant === "page_content" && !isPageDisabled(page),
                  })}
                >
                  {featuring && <FeatureModal />}
                  {isEmptyPanel && page ? (
                    <AssessmentSummaryMainPanel page={page} />
                  ) : page ? (
                    <PageDetailView
                      isStaticContent={pageIsStatic}
                      page={page}
                      selectedAccessId={selectedAccessId}
                      selectedTabVariant={tabVariant ?? undefined}
                    />
                  ) : (
                    <div>{t("page_detail_view.no_content")}</div>
                  )}
                </div>
              </div>
            </ProjectorScroller>
          </>
        )}
      </div>

      <ProjectorOverlay
        captionBgColor={captionBgColor}
        captionText={captionText}
        projectorHeight={projectorHeight}
        shouldShowProjectorOverlay={shouldShowProjectorOverlay}
      />

      {featuring && (
        // Need to add an overlay backdrop on the page content area of PageView during featuring
        // so that shrinking the screen does not leave an awkward sliver of white background.
        <div className="absolute inset-0 z-10 overflow-hidden rounded-[inherit] bg-black-tint-95/80 backdrop-blur-sm" />
      )}
    </main>
  );
};
